import { MaterialRequisitionModel } from "api/models/company/material-requisition/material-requisition-model";
import { autoinject, bindable } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import dateHelper from "helpers/dateHelper";
import notificationHelper from "helpers/notificationHelper";
import routerHelper from "helpers/routerHelper";
import materialRequisitionService from "services/materialRequisitionService";
import { RequisitionTabs } from "enums/requisition-tabs";
import { RequisitionProcessStatus } from "api/enums/requisition-process-status";
import userSettingService from "services/userSettingsService";

@autoinject
export class MaterialRequisition {

    public dateHelper: typeof dateHelper = dateHelper;
    public routerHelper: typeof routerHelper = routerHelper;
    public requisitionTabs: typeof RequisitionTabs = RequisitionTabs;
    public currentTabShown: number = RequisitionTabs.Active;
   
    public filterLabels: any[] = [];

    @bindable public actions: any = {};
    @bindable public isProjectResponsible: boolean = false;
    @bindable public readonly: boolean = false;

    @bindable public dispatchProjectCode: string = "";
    @bindable public dispatchId: string = "";
    @bindable public dispatchDate: string = "";
    @bindable public projectCode: string = "";
    @bindable public forProjectEquipment: boolean = false;
    @bindable public showFilterSection: boolean = false;
    @bindable public displayAllRequisitions: boolean = false;

    public requisitions: MaterialRequisitionModel[] = [];
    public requisitionsCompleted: MaterialRequisitionModel[] = [];
    public readonly requisitionProcessStatus: typeof RequisitionProcessStatus = RequisitionProcessStatus;

    constructor(private readonly i18n: I18N) {
    }

    public async bind(): Promise<any> {
        await this.loadData();
    }

    public attached(): any {
        if (this.showFilterSection) {
            this.formatToggleLabels(this.displayAllRequisitions);
        }
    }

    public genUrl(rel: string, requisitionId: string): string {
        return routerHelper.getRelativeUrl(rel + encodeURIComponent(requisitionId)) + routerHelper.addQuerystring({ isProjectResponsible: this.isProjectResponsible, readonly: this.readonly || this.currentTabShown === RequisitionTabs.Completed, dispatchProjectCode: this.dispatchProjectCode, projectCode: this.projectCode, dispatchId: this.dispatchId });
    }

    public gotoPage(): void {
        routerHelper.navigate(routerHelper.getRelativeUrl("edit") + routerHelper.addQuerystring({ isProjectResponsible: this.isProjectResponsible, readonly: this.readonly || this.currentTabShown === RequisitionTabs.Completed, dispatchProjectCode: this.dispatchProjectCode, projectCode: this.projectCode, dispatchId: this.dispatchId }));
    }

    public deleteRequisition(requisition: MaterialRequisitionModel): void {
        notificationHelper.showDeleteConfirmation().then((success: boolean) => {
            if (success) {
                if (requisition.MobileStatus !== "0") {
                    notificationHelper.showError(this.i18n.tr("RequisitionCompletedText"));
                } else {
                    routerHelper.showLoading();
                    materialRequisitionService.delMaterialRequisition(requisition.Id!).then(() => {
                        this.loadData();
                    });
                    routerHelper.hideLoading();
                }
            }
        });
    }

    public async show(section: number): Promise<void> {
        this.currentTabShown = section;
        await this.loadData();
    }

    public async toggle(section: number): Promise<void> {
        this.displayAllRequisitions = !this.displayAllRequisitions;

        if (!this.forProjectEquipment) {
            await userSettingService.setRequisitionsForCurrentUserSetting(this.displayAllRequisitions);
        } else {
            await userSettingService.setEquipmentRequisitionsForCurrentUserSetting(this.displayAllRequisitions);
        }
       
        this.formatToggleLabels(this.displayAllRequisitions);

        this.currentTabShown = section;

        await this.loadData();
    }

    public formatToggleLabels(checked: boolean): void {
        if (!this.filterLabels || this.filterLabels.length <= 0) {
            return;
        }

        const indexToActivate = checked ? 1 : 0;

        // Disable everything
        this.filterLabels.forEach((label: HTMLElement) => {
            label.classList.remove("bold");
            label.classList.add("text-muted");
        });

        // Enable the index received
        this.filterLabels[indexToActivate].classList.add("bold");
        this.filterLabels[indexToActivate].classList.remove("text-muted");
    }

    private async loadData(): Promise<void> {
        this.actions.getRequisitions(this.currentTabShown === RequisitionTabs.Active, this.displayAllRequisitions).then((data: MaterialRequisitionModel[]) => {
            if (this.currentTabShown === RequisitionTabs.Active) {
                this.requisitions = data;
            } else {
                this.requisitionsCompleted = data;
            }
        });
    }
}
